<script setup>
import BuildingsIcon from '@iconify-vue/ph/buildings';
import CurrencyDollarIcon from '@iconify-vue/ph/currency-dollar';
import EnvelopeSimpleIcon from '@iconify-vue/ph/envelope-simple';
import FileTextIcon from '@iconify-vue/ph/file-text';
import RobotIcon from '@iconify-vue/ph/robot';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatNumber, formatPrice } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const renderAiDetails = (details) =>
    h(
        'ul',
        { class: 'flex flex-col gap-4' },
        details.map((detail) =>
            h(
                'li',
                {
                    class: 'flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4',
                },
                [
                    h('div', { class: 'flex min-w-0 flex-col gap-1' }, [
                        h(
                            'span',
                            {
                                class: 'inline-flex w-fit items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700',
                            },
                            detail.agent,
                        ),
                        h(
                            'span',
                            { class: 'text-xs break-all text-gray-500' },
                            detail.model ?? 'Modèle inconnu',
                        ),
                        h(
                            'span',
                            { class: 'text-xs text-gray-400' },
                            `${formatNumber(detail.calls, false)} appel(s) · ${formatNumber(detail.tokens, false)} tokens`,
                        ),
                    ]),
                    h(
                        'span',
                        { class: 'text-lg font-bold whitespace-nowrap' },
                        formatPrice(detail.cost, true, 1, '$'),
                    ),
                ],
            ),
        ),
    );

const renderMailjetDetails = (details) =>
    h(
        'ul',
        { class: 'flex flex-col gap-4' },
        details.map((detail) =>
            h(
                'li',
                {
                    class: 'flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4',
                },
                [
                    h(
                        'span',
                        {
                            class: 'inline-flex w-fit items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700',
                        },
                        detail.source,
                    ),
                    h(
                        'span',
                        { class: 'text-lg font-bold whitespace-nowrap' },
                        `${formatNumber(detail.count, false)} email(s)`,
                    ),
                ],
            ),
        ),
    );

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 100,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'dataForSeoCosts',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coûts max<br/>DataForSEO',
                }),
            cell: (props) => {
                const details = props.row.original.dataForSeoDetails ?? [];

                return props.row.original.dataForSeoCosts > 0
                    ? h(
                          PriceCell,
                          {
                              price: props.row.original.dataForSeoCosts,
                              currency: '$',
                          },
                          {
                              actions: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Détails des coûts max DataForSEO',
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Voir le détails',
                                                  type: 'primary',
                                                  square: true,
                                                  icon: FileTextIcon,
                                              }),
                                          complexContent: () =>
                                              h(
                                                  'ul',
                                                  {
                                                      class: 'flex flex-col gap-4',
                                                  },
                                                  details.map((detail) =>
                                                      h(
                                                          'li',
                                                          {
                                                              class: 'flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4',
                                                          },
                                                          [
                                                              h(
                                                                  'div',
                                                                  {
                                                                      class: 'flex flex-col gap-1 min-w-0',
                                                                  },
                                                                  [
                                                                      detail.usedBy
                                                                          ? h(
                                                                                'span',
                                                                                {
                                                                                    class: 'inline-flex w-fit items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700',
                                                                                },
                                                                                detail.usedBy,
                                                                            )
                                                                          : null,
                                                                      h(
                                                                          'span',
                                                                          {
                                                                              class: 'text-xs break-all text-gray-500',
                                                                          },
                                                                          detail.url,
                                                                      ),
                                                                  ],
                                                              ),
                                                              h(
                                                                  'span',
                                                                  {
                                                                      class: 'text-lg font-bold whitespace-nowrap',
                                                                  },
                                                                  formatPrice(
                                                                      detail.cost,
                                                                      true,
                                                                      1,
                                                                      '$',
                                                                  ),
                                                              ),
                                                          ],
                                                      ),
                                                  ),
                                              ),
                                      },
                                  ),
                          },
                      )
                    : null;
            },
        }),
        columnHelper.accessor(() => null, {
            id: 'mailjetEmails',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Emails<br/>Mailjet',
                }),
            cell: (props) => {
                const details = props.row.original.mailjetDetails ?? [];

                return props.row.original.mailjetEmails > 0
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.mailjetEmails,
                          },
                          {
                              actions: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Détails des emails Mailjet',
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Voir le détail',
                                                  type: 'primary',
                                                  square: true,
                                                  icon: FileTextIcon,
                                              }),
                                          complexContent: () =>
                                              renderMailjetDetails(details),
                                      },
                                  ),
                          },
                      )
                    : null;
            },
        }),
        columnHelper.accessor(() => null, {
            id: 'aiCosts',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coûts IA',
                }),
            cell: (props) => {
                const details = props.row.original.aiDetails ?? [];

                return details.length
                    ? h(
                          PriceCell,
                          {
                              price: props.row.original.aiCosts,
                              currency: '$',
                          },
                          {
                              actions: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Détails de la consommation IA',
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Voir le détail',
                                                  type: 'primary',
                                                  square: true,
                                                  icon: FileTextIcon,
                                              }),
                                          complexContent: () =>
                                              renderAiDetails(details),
                                      },
                                  ),
                          },
                      )
                    : null;
            },
        }),
    ],
    url: route('front.projectManagement.customer.postExternalServiceUsages'),
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #before-table>
            <CardGrid>
                <MetricCard
                    title="DataForSEO"
                    subtitle="Coûts max DataForSEO"
                    :value="globalData.totalDataForSeoCosts"
                    type="price"
                    :with-decimals="true"
                    :icon="CurrencyDollarIcon"
                    currency="$"
                />

                <MetricCard
                    title="Mailjet"
                    subtitle="Emails envoyés"
                    :value="globalData.totalMailjetEmails"
                    type="number"
                    :with-decimals="false"
                    :icon="EnvelopeSimpleIcon"
                />

                <MetricCard
                    title="IA"
                    subtitle="Coûts IA estimés"
                    :value="globalData.totalAiCosts"
                    type="price"
                    :with-decimals="true"
                    :icon="RobotIcon"
                    currency="$"
                />

                <MetricCard
                    title="Apikom"
                    subtitle="Coûts IA Apikom"
                    :value="globalData.apikomAiCosts"
                    type="price"
                    :with-decimals="true"
                    :icon="BuildingsIcon"
                    currency="$"
                >
                    <template
                        v-if="globalData.apikomAiDetails?.length"
                        #actions
                    >
                        <DisplayContentAction
                            title="Détails de la consommation IA Apikom"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    title="Voir le détail"
                                    type="primary"
                                    square
                                    :icon="FileTextIcon"
                                />
                            </template>

                            <template #complexContent>
                                <component
                                    :is="
                                        () =>
                                            renderAiDetails(
                                                globalData.apikomAiDetails,
                                            )
                                    "
                                />
                            </template>
                        </DisplayContentAction>
                    </template>
                </MetricCard>
            </CardGrid>
        </template>
    </DataTableLayout>
</template>
